import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ProcessorDetails } from '@/lib/api/processors';
import { ProcessorHeader } from '../components/processor-header';
import { ProcessorOverviewCard } from '../components/processor-overview-card';
import { ProcessorMetricsCard } from '../components/processor-metrics-card';
import { ProcessorPaymentMethodsCard } from '../components/processor-payment-methods-card';

type Props = {
  processor: ProcessorDetails;
};

const MASKED = '••••••••••••••••';
const ATTEMPT_THREE_D = ['3D', 'Only3D', 'Direct'] as const;

const readString = (options: Record<string, unknown> | null | undefined, key: string): string | undefined => {
  const value = options?.[key];
  return typeof value === 'string' && value !== '' ? value : undefined;
};

const XoalaConfigurationCard = ({ processor }: Props) => {
  const { t } = useTranslation('processors');
  const options = processor.options;
  const memberId = readString(options, 'memberId');
  const terminalId = readString(options, 'terminalId');
  const attemptThreeD = ATTEMPT_THREE_D.find((mode) => mode === options?.attemptThreeD) ?? '3D';
  const isTestMode = options?.testMode === true;
  const notConfigured = <span className="text-muted-foreground italic">{t('notConfigured')}</span>;

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>{t('xoala.configurationTitle')}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <p className="text-muted-foreground text-sm">{t('xoala.credentials.memberIdLabel')}</p>
            <p className="font-mono text-sm">{memberId ?? notConfigured}</p>
          </div>
          <div>
            <p className="text-muted-foreground text-sm">{t('xoala.credentials.terminalIdLabel')}</p>
            <p className="font-mono text-sm">{terminalId ?? notConfigured}</p>
          </div>
          <div>
            <p className="text-muted-foreground text-sm">{t('xoala.credentials.secretKeyLabel')}</p>
            <p className="font-mono text-sm">{MASKED}</p>
          </div>
          <div>
            <p className="text-muted-foreground text-sm">{t('xoala.threeDS.label')}</p>
            <p className="text-sm">{t(`xoala.threeDS.options.${attemptThreeD}.label`)}</p>
          </div>
          <div>
            <p className="text-muted-foreground text-sm">{t('environment')}</p>
            <Badge variant={isTestMode ? 'outline' : 'default'}>
              {isTestMode ? t('sandbox') : t('production')}
            </Badge>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const XoalaProcessorDetailsPage = ({ processor }: Props) => (
  <>
    <ProcessorHeader type="xoala" processor={processor} />
    <ProcessorOverviewCard processor={processor} />
    <ProcessorMetricsCard processor={processor} />
    <XoalaConfigurationCard processor={processor} />
    <ProcessorPaymentMethodsCard processor={processor} />
  </>
);
